import React from 'react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth'; // আপনার Auth hook
import { Check, Star, Zap } from 'lucide-react';

const VITE_API_URL = import.meta.env.VITE_API_URL;

const SubscriptionPlans = () => {
    const { token } = useAuth(); // Auth token নিন

    const handlePayment = async (amount, planType) => {
        try {
            const response = await axios.post(
                `${VITE_API_URL}/subscription/pay`,
                { amount, planType },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.bkashURL) {
                // bKash এর পেমেন্ট পেজে নিয়ে যাবে
                window.location.href = response.data.bkashURL;
            }
        } catch (error) {
            alert("Payment initiation failed!");
            console.error(error);
        }
    };

    return (
        <div className="min-h-screen bg-linear-to-br from-indigo-50 to-blue-100 py-16 px-4">
            <div className="max-w-5xl mx-auto text-center mb-12">
                <h2 className="text-indigo-600 font-bold tracking-wide uppercase text-sm mb-3">Pricing Plans</h2>
                <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                    Upgrade Your Practice Today
                </h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    Choose the plan that fits your needs. Start generating unlimited prescriptions and managing patients efficiently.
                </p>
            </div>

            <div className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-5xl mx-auto">
                
                {/* --- MONTHLY PLAN --- */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 w-full md:w-80 relative transform transition hover:-translate-y-2 hover:shadow-2xl">
                    <div className="absolute top-0 right-0 bg-indigo-100 text-indigo-600 text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                        POPULAR
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">Monthly</h3>
                    <p className="text-gray-500 text-sm mt-1">Perfect for getting started</p>
                    
                    <div className="my-6">
                        <div className="flex items-center justify-center gap-2 text-gray-400 line-through text-lg">
                            ৳ 1,500
                        </div>
                        <div className="flex items-center justify-center gap-1">
                            <span className="text-2xl font-bold text-gray-800">৳</span>
                            <span className="text-5xl font-extrabold text-indigo-600">1000</span>
                            <span className="text-gray-500 font-medium self-end mb-1">/mo</span>
                        </div>
                        <div className="mt-2 text-xs font-bold text-green-600 bg-green-50 inline-block px-3 py-1 rounded-full">
                            Save ৳ 500
                        </div>
                    </div>

                    <button 
                        onClick={() => handlePayment(1000, 'monthly')}
                        className="w-full py-3 px-6 rounded-xl bg-indigo-50 text-indigo-700 font-bold hover:bg-indigo-100 transition duration-200"
                    >
                        Choose Monthly
                    </button>

                    <ul className="mt-8 space-y-4 text-left text-sm text-gray-600">
                        <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5" /> Unlimited Prescriptions</li>
                        <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5" /> Patient Management</li>
                        <li className="flex items-center gap-3"><Check className="text-green-500 w-5 h-5" /> Basic Analytics</li>
                    </ul>
                </div>

                {/* --- YEARLY PLAN (HIGHLIGHTED) --- */}
                <div className="bg-gray-900 rounded-2xl shadow-2xl border-2 border-indigo-500 p-8 w-full md:w-96 relative transform scale-105 z-10">
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-linear-to-r from-pink-500 to-purple-600 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg flex items-center gap-1">
                        <Star className="w-4 h-4 fill-current" /> BEST VALUE
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white">Yearly Pro</h3>
                    <p className="text-gray-400 text-sm mt-1">For committed professionals</p>
                    
                    <div className="my-6">
                        <div className="flex items-center justify-center gap-2 text-gray-500 line-through text-lg">
                            ৳ 18,000
                        </div>
                        <div className="flex items-center justify-center gap-1 text-white">
                            <span className="text-2xl font-bold">৳</span>
                            <span className="text-5xl font-extrabold">9,900</span>
                            <span className="text-gray-400 font-medium self-end mb-1">/yr</span>
                        </div>
                        <div className="mt-2 text-xs font-bold text-gray-900 bg-yellow-400 inline-block px-3 py-1 rounded-full animate-pulse">
                            🔥 Save ৳ 8,100
                        </div>
                    </div>

                    <button 
                        onClick={() => handlePayment(9900, 'yearly')}
                        className="w-full py-4 px-6 rounded-xl bg-linear-to-r from-pink-600 to-purple-600 text-white font-bold text-lg hover:from-pink-700 hover:to-purple-700 shadow-lg transition duration-200 flex items-center justify-center gap-2"
                    >
                        <Zap className="w-5 h-5 fill-current" /> Pay with bKash
                    </button>

                    <div className="mt-4 text-center">
                        <p className="text-xs text-gray-400">Equivalent to ৳ 825 / month</p>
                    </div>

                    <ul className="mt-8 space-y-4 text-left text-sm text-gray-300">
                        <li className="flex items-center gap-3"><div className="bg-green-500/20 p-1 rounded-full"><Check className="text-green-400 w-4 h-4" /></div> All Monthly Features</li>
                        <li className="flex items-center gap-3"><div className="bg-green-500/20 p-1 rounded-full"><Check className="text-green-400 w-4 h-4" /></div> Priority Support</li>
                        <li className="flex items-center gap-3"><div className="bg-green-500/20 p-1 rounded-full"><Check className="text-green-400 w-4 h-4" /></div> Advanced Reports & AI</li>
                        <li className="flex items-center gap-3"><div className="bg-green-500/20 p-1 rounded-full"><Check className="text-green-400 w-4 h-4" /></div> Personal Website Profile</li>
                    </ul>
                </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-16 text-center">
                <p className="text-gray-500 text-sm mb-4">Trusted securely by 500+ Doctors</p>
                <div className="flex justify-center gap-6 opacity-60 grayscale">
                    <img src="https://freelogopng.com/images/all_img/1656234745bkash-app-logo-png.png" alt="bKash" className="h-8" />
                    {/* Add other logos like SSLCommerz if needed */}
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPlans;